import React, { useCallback } from"react";
import { useMediaQuery } from"@/shared/hooks/use-media-query";
import { SelectErrorMessages, MobileOverlay, DesktopDropdown } from"./Select.parts";
import {
  cn, HIDDEN_INPUT_STYLE, useClickOutsideClose, useMobileAutoFocus, useMobileOverlayBodyLock,
} from"./Select.helpers";

interface InputModeProps {
  state: any;
  refs: any;
  name: string;
  labelText: string;
  required: boolean;
  autofocus: boolean;
  immediateValidation: boolean;
  errors: Set<string>;
  submitted: boolean;
  toPresentErrors: boolean;
  allErrorMessages: string[];
  classNameProp?: string;
  styleProp?: React.CSSProperties;
  getDisplayNameForValue: (v: string) => string;
  renderOption?: (optionValue: string, selectedValue: string, isActive: boolean) => React.ReactNode;
  renderEmptyState?: () => React.ReactNode;
  nothingFoundMessage?: string;
  t: (k: string, o?: any) => string;
  handleKeyDown: (e: React.KeyboardEvent<HTMLInputElement>, setIsFocused: (v: boolean) => void) => void;
  resetKeyboardNavigation: () => void;
}

const SelectInputMode: React.FC<InputModeProps> = ({
  state, refs, name, labelText, required, autofocus, immediateValidation, errors, submitted,
  toPresentErrors, allErrorMessages, classNameProp, styleProp, getDisplayNameForValue,
  renderOption, renderEmptyState, nothingFoundMessage, t, handleKeyDown, resetKeyboardNavigation,
}) => {
  const { containerRef, inputRef, mobileInputRef, portalInteractingRef } = refs;
  const isMobile = useMediaQuery('(max-width: 767px)');

  const wrappedHandleKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    handleKeyDown(e, state.setIsFocused);
  }, [handleKeyDown, state.setIsFocused]);

  const wrappedOnFocus = useCallback(() => { resetKeyboardNavigation(); state.onFocus(); }, [resetKeyboardNavigation, state.onFocus]);

  const handleInputBlur = useCallback(() => {
    setTimeout(() => {
      if (portalInteractingRef.current) return;
      if (containerRef.current && !containerRef.current.contains(document.activeElement)) state.onBlur();
    }, 150);
  }, [state.onBlur, containerRef, portalInteractingRef]);

  useClickOutsideClose(state.isFocused, containerRef, state.onBlur);

  const shouldShowMobileOverlay = isMobile && state.isFocused;
  useMobileOverlayBodyLock(shouldShowMobileOverlay);
  useMobileAutoFocus(shouldShowMobileOverlay, mobileInputRef);

  const renderOptionsList = useCallback(() => {
    if (state.filteredOptions.length === 0) {
      return renderEmptyState ? renderEmptyState() : (
        <div className="p-2 text-center text-[var(--text-tertiary)]">{nothingFoundMessage || t('form.messages.nothingFound')}</div>
      );
    }
    return state.filteredOptions.map((optVal: string, index: number) => {
      const isActive = index === state.activeOptionIndex;
      if (renderOption) {
        const custom = renderOption(optVal, state.selectedValue, isActive);
        if (custom) return (
          <div key={optVal} onMouseDown={(e) => { e.preventDefault(); state.ensureDisplayNameInQuery(optVal); }}
            onClick={() => state.onSelect(optVal)} className="cursor-pointer">{custom}</div>
        );
      }
      const bgKey = isActive ?'active' : optVal === state.selectedValue ?'selected' :'default';
      const bgClass = { active:'bg-[var(--selection-active)]', selected:'bg-[var(--selection-selected)]', default:'hover:bg-[var(--surface-hover)]' }[bgKey];
      return (
        <div key={optVal} className={`px-3 py-2 cursor-pointer text-sm text-[var(--text-primary)] ${bgClass}`}
          onMouseDown={(e) => { e.preventDefault(); state.ensureDisplayNameInQuery(optVal); }}
          onClick={() => state.onSelect(optVal)}>
          {getDisplayNameForValue(optVal)}
        </div>
      );
    });
  }, [state.filteredOptions, state.selectedValue, state.activeOptionIndex, renderOption, renderEmptyState, state.ensureDisplayNameInQuery, state.onSelect, getDisplayNameForValue, nothingFoundMessage, t]);

  const hasErrors = errors.size > 0;
  const borderClass = hasErrors ?"border-[var(--status-error-border)]" :"border-[var(--border-medium)]";
  const errorDescribedBy = hasErrors ?`${name}-error` : undefined;

  return (
    <div ref={containerRef} className={cn("relative w-full", classNameProp)} style={{ minWidth: 0, ...styleProp }}>
      <label className={`block text-sm leading-tight mb-1.5 ${hasErrors ?"text-[var(--status-error-text)]" :"text-[var(--text-primary)]"}`} htmlFor={name}>
        {labelText}{required && <span className="text-[var(--seed-base)] ml-0.5"> *</span>}
      </label>
      <div className="relative" style={shouldShowMobileOverlay ? HIDDEN_INPUT_STYLE : {}}>
        <input ref={inputRef}
          className={`auth-input w-full h-10 px-3 py-2 text-sm bg-[var(--surface-primary)] text-[var(--text-primary)] focus:outline-none focus:border-[var(--seed-base)] placeholder:text-[var(--text-tertiary)] transition-colors duration-200 border ${borderClass}`}
          placeholder={labelText} autoFocus={autofocus && !shouldShowMobileOverlay} required={required}
          data-immediate-validation={immediateValidation} name={name} id={name}
          onChange={(e) => state.handleQueryChange(e.target.value)} autoComplete="off"
          onClick={() => state.setIsFocused(true)} onFocus={wrappedOnFocus} onBlur={handleInputBlur}
          onKeyDown={wrappedHandleKeyDown} value={state.inputValue} aria-describedby={errorDescribedBy}
          tabIndex={shouldShowMobileOverlay ? -1 : 0} data-selected-value={state.selectedValue} />
      </div>
      <MobileOverlay show={shouldShowMobileOverlay} mobileInputRef={mobileInputRef} errors={errors}
        children={labelText} required={required} immediateValidation={immediateValidation} name={name}
        handleQueryChange={state.handleQueryChange} wrappedHandleKeyDown={wrappedHandleKeyDown}
        query={state.query} renderOptionsList={renderOptionsList} onClose={state.onBlur} />
      <DesktopDropdown shouldShowMobileOverlay={shouldShowMobileOverlay} isFocused={state.isFocused} renderOptionsList={renderOptionsList} anchorRef={containerRef} interactingRef={portalInteractingRef} />
      <SelectErrorMessages errors={allErrorMessages} name={name} submitted={submitted} toPresentErrors={toPresentErrors} />
    </div>
  );
};

export default SelectInputMode;
